import { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { useStaticQuery, graphql } from "gatsby";

import { ALLink as Link } from "@components/ALComponents";

import { GeoContext } from "@context/GeoProvider";

import { gaCustomEvent } from "@helpers/browser";
import { edgesToArray } from "@helpers/general";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";

import IconArrowRight from "@svgs/arrow-right.svg";
import Customizable from "@images/custom.png";

function CategoryMenu({
  activateCategoryMenu,
  categoryMenuStatus,
  initializeMenuStatuses,
  closeMenu,
}) {
  const { gePriceDetails } = useContext(GeoContext);
  const categoryMenu = useStaticQuery(graphql`
    query CategoryMenu {
      menuWrapper: allContentfulMenuWrapper {
        edges {
          node {
            node_locale
            countries {
              code
            }
            mobileMenuItems {
              id
              node_locale
              title
              textColor
              backgroundColor
              menuContainer {
                menuCategory {
                  ... on ContentfulMenuCategory {
                    id
                    title
                    titleLink
                    mobileMenuItems {
                      link {
                        title
                        link {
                          id
                          link
                        }
                      }
                    }
                  }
                  ... on ContentfulText {
                    __typename
                    id
                    text
                    textColor {
                      mobile
                      desktop
                    }
                    fontWeight
                  }
                }
              }
            }
          }
        }
      }
    }
  `);

  const { menuWrapper: allContentfulMenuWrapper } = categoryMenu;

  const onMenuLinkClick = (category, subCategory) => {
    const eventLabel = subCategory ? `${category} -> ${subCategory}` : `${category}`;
    gaCustomEvent("Menu Link", "Click", eventLabel);
    closeMenu();
  };

  // Use this to keep track of which submenu is expanded for a11y reasons
  const initialCategoryMenuStatus = {};

  let menus = null;
  if (allContentfulMenuWrapper?.edges && allContentfulMenuWrapper?.edges.length > 0) {
    const allMenuArray = edgesToArray(allContentfulMenuWrapper);
    const defaultMenu = allMenuArray.find((n) => n?.countries?.some((c) => c.code === "RoW"));
    const localeSpecificMenu = allMenuArray.filter((n) =>
      n?.countries?.some((c) => c.code === gePriceDetails.CountryCode)
    );
    const localeSpecificMenuRoot = localeSpecificMenu.find(
      (b) => b.node_locale === gePriceDetails.Culture
    );
    menus = localeSpecificMenuRoot?.mobileMenuItems ?? defaultMenu.mobileMenuItems;
  }

  const categoryMenus = menus.map((menu) => {
    const menuContainer = menu.menuContainer[0].menuCategory;
    const menuCategory = menu.menuContainer[0].menuCategory[0];
    // If the category doesn't have a sub-menu (category menu), just return a link
    if (menuContainer.length === 1) {
      return (
        <Link
          key={menu.id}
          className="typo-h3-bold p-[15px] flex justify-between"
          to={menuCategory.titleLink}
          onClick={() => onMenuLinkClick(menuCategory.title)}
          style={{
            color: menu.textColor || "#000000",
            backgroundColor: menu.backgroundColor,
          }}
        >
          <p>{menuCategory.title}</p>
          <IconArrowRight height={16} fill="none" stroke={menu.textColor || "#000000"} />
        </Link>
      );
    }

    initialCategoryMenuStatus[menuCategory.title] = false;

    // The items that will be contained in the sub-menu (category menu)
    let isFirstHeader = true;
    const menuCategoryItemsList = menuContainer.map((item) => {
      if (item.mobileMenuItems?.length) {
        return null;
      }

      if (item.__typename === "ContentfulText" && item.text !== "null") {
        const marginClass = isFirstHeader ? "mt-[30px]" : "mt-[50px]";
        isFirstHeader = false;
        return (
          <p
            key={item.id}
            style={{ fontWeight: item.fontWeight, color: item.textColor?.mobile || "#000000" }}
            className={`flex items-center gap-[7px] typo-h3 px-[15px] ${marginClass}`}
          >
            {item.text.toLowerCase().includes("personalized") && (
              <img
                className="w-[17px] h-[17px]"
                src={Customizable}
                alt="Customizable"
                loading="lazy"
              />
            )}
            {item.text}
          </p>
        );
      }

      // Normal 2-layer subcategory
      return (
        <Link
          className="typo-h3 px-[15px] pt-[20px]"
          to={item.titleLink}
          key={item.id}
          onClick={() => onMenuLinkClick(menuCategory.title, item.title)}
        >
          {item.title}
        </Link>
      );
    });

    // We are not rendering the 3rd layer for now,
    // but keeping the logic in place in case we want to re-enable it in the future

    const menuCategoryComponent = menuCategoryItemsList;

    return (
      <button
        key={menu.id}
        type="button"
        className="typo-h3-bold p-[15px] flex justify-between"
        onClick={() => activateCategoryMenu(menuCategoryComponent, menu.title)}
        aria-expanded={categoryMenuStatus?.[menu.title]}
        style={{
          color: menu.textColor || "#000000",
          backgroundColor: menu.backgroundColor,
        }}
      >
        <div className="flex items-center gap-[4px]">
          {menu.title === "Custom" && (
            <img
              className="mobile:w-[12px] mobile:h-[12px]"
              src={Customizable}
              alt="Customizable"
              loading="lazy"
            />
          )}
          <p>{menu.title}</p>
        </div>

        <IconArrowRight height={16} fill="none" stroke={menu.textColor || "#000000"} />
      </button>
    );
  });

  useEffect(() => {
    initializeMenuStatuses(categoryMenuStatus);
  }, []);

  return <div className="flex flex-col overflow-y-auto flex-1 min-h-0">{categoryMenus}</div>;
}

CategoryMenu.propTypes = {
  activateCategoryMenu: PropTypes.func,
  categoryMenuStatus: PropTypes.object,
  closeMenu: PropTypes.func.isRequired,
};

export default withALErrorBoundary({
  name: "CategoryMenu",
  priority: "P2",
})(CategoryMenu);
